import { Injectable } from '@jkhy/frontend-utils/di';
import { getKeys } from '@treasury/utils/functions';
import { html, type TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import type { TmDialog } from './tm-dialog';
import { TmDialogService } from './tm-dialog.service';
import {
    listenForDialogResult,
    TmDialogButton,
    TmDialogButtonMap,
    TmDialogContent,
    TmDialogServiceConfig,
    TmDialogServiceConfigFull,
} from './tm-dialog.service.types';

// eslint-disable-next-line no-duplicate-imports
import './tm-dialog';

/**
 * A modern dialog service implementation for use in applications
 * that support `TmBaseComponent` (i.e., Tailwind).
 */
@Injectable()
export class ModernDialogService extends TmDialogService<TmDialog> {
    private readonly contentRef = createRef();

    public open<T>(
        content: TmDialogContent<T>,
        title: string,
        options: TmDialogServiceConfig<T> = {}
    ) {
        if (this.isDialogOpen) {
            throw new Error(
                'Attempted to open a dialog when one is already open. Close the existing dialog'
            );
        }

        content = typeof content === 'function' ? content(data => this.setData(data)) : content;

        const allOptions = this.normalizeConfig(options);
        const handle = this.registry.create<T>(
            () => this.render(content, title, allOptions),
            allOptions
        );
        listenForDialogResult<T>(handle.element, ({ reason, data }) => {
            this.close(reason, data);
        });

        this.handle = handle;
        return handle.disposed;
    }

    /**
     * Get a reference to the element being hosted by the dialog.
     */
    public getContent<Elem extends Element = Element>() {
        const { value } = this.contentRef;
        if (!value) {
            throw new Error('Could not get dialog content. No dialog is open.');
        }

        return value as Elem;
    }

    private setData<T>(data: T) {
        if (!this.handle) {
            throw new Error('Cannot set dialog data. No dialog is open.');
        }

        this.handle.element.data = data;
    }

    private renderContent(content: string | TemplateResult) {
        // wrap content in outer element for use with ref() directive
        return typeof content === 'string'
            ? html`<p ${ref(this.contentRef)} class="py-4 text-sm">${content}</p>`
            : html`<div ${ref(this.contentRef)}>${content}</div>`;
    }

    private render<T>(
        content: string | TemplateResult,
        title: string,
        options: TmDialogServiceConfigFull<T>
    ) {
        content = this.renderContent(content);
        const {
            preventClose,
            showButtons,
            showDivider,
            classes,
            headerIcon,
            orientation,
            showCloseButton,
        } = options;
        const buttons = Array.isArray(options.buttons)
            ? options.buttons
            : convertPrefabButtons(options.buttons);

        return html`<tm-dialog
            .header=${title}
            .content=${content}
            .classes=${classes}
            .buttons=${buttons as TmDialogButton[]}
            .headerIcon=${headerIcon}
            .preventClose=${preventClose}
            .showDivider=${showDivider}
            .showButtons=${showButtons}
            .showCloseButton=${showCloseButton}
            orientation=${ifDefined(orientation)}
        >
        </tm-dialog>`;
    }
}

/**
 * Normalize a dictionary of prefab buttons to an array of standard buttons.
 */
function convertPrefabButtons(buttons: TmDialogButtonMap): TmDialogButton[] {
    return getKeys(buttons).map(posture => {
        const { label, reason } = buttons[posture];
        return {
            reason,
            posture,
            label,
            onClick: close => close(),
        };
    });
}
